import { Routes } from "@angular/router";
import { AuthGuard } from "@auth0/auth0-angular";
import { ClaimsComponent } from "./claims/claims/claims.component";
import { AsmPermissionGuard } from "./core/guards/asm.guard";
import { EdiFilesComponent } from "./developers/edi/edi-files.component";
import { EdiFileDetailComponent } from "./developers/edi/file-details/edi-file-detail.component";
import { EdiLoadDetailComponent } from "./developers/edi/load-details/edi-load-detail.component";
import { Permissions } from "./shared/common/permissions";
import { ShipmentDetailsComponent } from "./shipments/details/details.component";
import { InboundShipmentsComponent } from "./shipments/inbound/inbound-shipments.component";
import { ShipmentListComponent } from "./shipments/list/shipments.component";
import { ShipmentQuoteDetailComponent } from "./shipments/quotes/details/quote-detail.component";
import { ShipmentQuoteListComponent } from "./shipments/quotes/list/shipment-quote-list.component";
import { QuoteScheduledComponent } from "./shipments/quotes/quote-scheduled/quote-scheduled.component";
import { RealtimeInboundShipmentsComponent } from "./shipments/realtime-inbound/realtime-inbound-shipments.component";
import { ShipmentImportComponent } from "./shipments/shipment-import/shipment-import.component";
import { AsmInternalGuard } from "./core/guards/asm-internal.guard";

export const routes: Routes = [
  // Default/Dashboard Routes (/)
  {
    path: "",
    canActivate: [AuthGuard],
    loadComponent: () => import("./dashboard/dashboard/dashboard.component").then(m => m.DashboardComponent),
  },
  { path: 'mailbox', loadChildren: () => import('./mailbox/mailbox.routes') },
  // Admin Routes (/admin)
  {
    path: "admin",
    loadChildren: () => [
      {
        path: "customers",
        loadComponent: () => import("./admin/customers/list/customers-list.component").then(m => m.CustomersListComponent),
        canActivate: [AuthGuard, AsmPermissionGuard],
        data: { permission: [Permissions.CUSTOMERS_MANAGE] }
      },
      {
        path: "customers/:id",
        loadComponent: () => import("./admin/customers/detail/customer-detail/customer-detail.component").then(m => m.CustomerDetailComponent),
        canActivate: [AuthGuard, AsmPermissionGuard],
        data: { permission: [Permissions.CUSTOMERS_MANAGE] }
      },
      {
        path: "users",
        loadComponent: () => import("./admin/users/list/users.component").then(m => m.UsersComponent),
        canActivate: [AuthGuard, AsmPermissionGuard],
        data: { permission: [Permissions.USERS_MANAGEEXTERNAL, Permissions.USERS_MANAGEALL, Permissions.DEV_ALL], isStrict: true }
      },
      {
        path: "invoicing/generated",
        loadComponent: () => import("./admin/invoicing/runs/invoiceruns.component").then(m => m.InvoiceRunsComponent),
        canActivate: [AuthGuard, AsmPermissionGuard],
        data: { permission: [Permissions.ACCOUNTING_VIEWINVOICES, Permissions.ACCOUNTING_BILLLOAD, Permissions.ACCOUNTING_INVOICELOAD] }
      },
      {
        path: "invoicing/invoice/:invoiceId",
        loadComponent: () => import("./admin/invoicing/batches/details/invoice-batch-detail.component").then(m => m.InvoiceBatchDetailComponent),
        canActivate: [AuthGuard, AsmPermissionGuard],
        data: { permission: [Permissions.ACCOUNTING_VIEWINVOICES, Permissions.ACCOUNTING_BILLLOAD, Permissions.ACCOUNTING_INVOICELOAD] }
      },
      {
        path: "invoicing/approvals",
        loadComponent: () => import("./admin/invoicing/approvals/approvals.component").then(m => m.ApprovalsComponent),
        canActivate: [AuthGuard, AsmPermissionGuard],
        data: { permission: [Permissions.ACCOUNTING_VIEWINVOICES, Permissions.ACCOUNTING_BILLLOAD, Permissions.ACCOUNTING_INVOICELOAD] }
      },
      {
        path: "nmfcs",
        loadComponent: () => import("./admin/nmfcs/list/nmfcs.component").then(m => m.NMFCsComponent),
        canActivate: [AuthGuard, AsmPermissionGuard],
        data: { permission: [Permissions.NMFCS_MANAGE] }
      },
      {
        path: "pricing",
        loadComponent: () => import("./admin/pricing/pricing.component").then(m => m.PricingComponent),
        canActivate: [AuthGuard, AsmPermissionGuard],
        data: { permission: [Permissions.ACCOUNTING_MANAGERATES, Permissions.ACCOUNTING_VIEWRATES], isStrict: true }
      },
      {
        path: "rates",
        redirectTo: "pricing",
        pathMatch: "full"
      },
      {
        path: "rates/customers/:customerId",
        loadComponent: () => import("./admin/rates/ftl-editor/ftl-editor.component").then(m => m.FTLEditorComponent),
        canActivate: [AuthGuard, AsmPermissionGuard],
        data: { permission: [Permissions.ACCOUNTING_MANAGERATES, Permissions.DEV_ALL], isStrict: true }
      },
      {
        path: "rates/customers/:customerId/history",
        loadComponent: () => import("./admin/rates/history/rate-history.component").then(m => m.RateHistoryComponent),
        canActivate: [AuthGuard, AsmPermissionGuard],
        data: { permission: [Permissions.ACCOUNTING_MANAGERATES, Permissions.ACCOUNTING_VIEWRATES, Permissions.DEV_ALL], isStrict: true }
      },
      {
        path: "bulk-custom-loads",
        loadComponent: () => import("./admin/bulk-custom-loads/bulk-custom-loads.component").then(m => m.BulkCustomLoadsComponent),
        canActivate: [AuthGuard, AsmPermissionGuard],
        data: { permission: [Permissions.SHIPMENTS_IMPORTCUSTOM] }
      },
      {
        path: "edi",
        loadComponent: () => import("./admin/edi/edi.component").then(m => m.EdiComponent),
        canActivate: [AuthGuard, AsmPermissionGuard],
        data: { permission: [Permissions.ACCOUNTING_EDIVARIANCES] }
      },
      {
        path: "user-summary",
        loadComponent: () => import("./user-summary/user-summary.component").then(m => m.UserSummaryComponent),
        canActivate: [AuthGuard, AsmPermissionGuard],
        data: { permission: [Permissions.USERS_MANAGEEXTERNAL, Permissions.USERS_MANAGEALL, Permissions.DEV_ALL], isStrict: true }
      },
      {
        path: "assets",
        loadComponent: () => import("./admin/assets/assets.component").then(m => m.AssetsComponent),
        canActivate: [AuthGuard, AsmPermissionGuard],
        data: { permission: [Permissions.OTHER_ASSETINVENTORY] }
      },
      {
        path: "ups-invoice-import",
        loadComponent: () => import("./admin/ups-invoice-import/ups-invoice-import.component").then(m => m.UpsInvoiceImportComponent),
        canActivate: [AuthGuard, AsmPermissionGuard],
        data: { permission: [Permissions.ACCOUNTING_UPSIMPORT] }
      },
      {
        path: "auto-quote-report",
        loadComponent: () => import("./admin/auto-quote-report/auto-quote-report.component").then(m => m.AutoQuoteReportComponent),
        canActivate: [AuthGuard, AsmPermissionGuard],
        data: { permission: [Permissions.REPORTS_AUTOQUOTE] }
      },
      {
        path: "billing/dashboard",
        loadComponent: () => import("./admin/billing/dashboard/billing-dashboard.component").then(m => m.BillingDashboardComponent),
        canActivate: [AuthGuard, AsmPermissionGuard],
        data: { permission: [Permissions.ACCOUNTING_VIEWINVOICES, Permissions.ACCOUNTING_BILLLOAD, Permissions.ACCOUNTING_INVOICELOAD, Permissions.DEV_ALL], isStrict: true }
      },
      {
        path: "billing/invoices",
        loadComponent: () => import("./admin/billing/invoices/billing-invoice-search.component").then(m => m.BillingInvoiceSearchComponent),
        canActivate: [AuthGuard, AsmPermissionGuard],
        data: { permission: [Permissions.ACCOUNTING_VIEWINVOICES, Permissions.ACCOUNTING_BILLLOAD, Permissions.ACCOUNTING_INVOICELOAD, Permissions.DEV_ALL], isStrict: true }
      },
      {
        path: "billing/invoices/:invoiceId",
        loadComponent: () => import("./admin/billing/invoices/billing-invoice-detail.component").then(m => m.BillingInvoiceDetailComponent),
        canActivate: [AuthGuard, AsmPermissionGuard],
        data: { permission: [Permissions.ACCOUNTING_VIEWINVOICES, Permissions.ACCOUNTING_BILLLOAD, Permissions.ACCOUNTING_INVOICELOAD, Permissions.DEV_ALL], isStrict: true }
      }
    ],
  },

  // Carrier Routes (/carriers)
  {
    path: "carriers",
    canActivate: [AuthGuard],
    loadChildren: () =>
      [
        {
          path: "summary",
          loadComponent: () => import("./carriers/summary/carrier-summary.component").then(m => m.CarrierSummaryComponent)
        },
        // {
        //   path: "lookup",
        //   loadComponent: () => import("./carriers/lookup/carrier-lookup.component").then(m => m.CarrierLookupComponent),
        //   canActivate: [AuthGuard, AsmPermissionGuard],
        //   data: { permission: [Permissions.DEV_ALL] },
        // },
        {
          path: "accounts",
          loadComponent: () => import("./admin/carrier-accounts/carrier-accounts.component").then(m => m.CarrierAccountsComponent),
          canActivate: [AuthGuard, AsmPermissionGuard],
          data: { permission: [Permissions.CARRIERACCOUNTS_VIEW, Permissions.CARRIERACCOUNTS_MANAGE, Permissions.DEV_ALL], isStrict: true },
        },
        {
          path: "prepro-summary",
          loadComponent: () => import("./prepro/prepro-summary.component").then(m => m.PreProSummaryComponent),
          canActivate: [AuthGuard, AsmPermissionGuard],
          data: {
            permission: [Permissions.CARRIERS_PREPRO],
          },
        },
        {
          path: ":id",
          canActivate: [AuthGuard, AsmPermissionGuard],
          loadComponent: () => import("./carriers/detail/carrier-detail.component").then(m => m.CarrierDetailComponent),
          data: { permission: [Permissions.CARRIERS_MANAGE] },
        },
      ],
  },

  // Compliance Routes (/compliance)
  {
    path: "compliance",
    canActivate: [AuthGuard, AsmPermissionGuard],
    data: { permission: [Permissions.COMPLIANCE_MANAGE] },
    loadChildren: () => import("./compliance/compliance.routes").then(m => m.complianceRoutes)
  },

  // Claims Routes (/claims)
  {
    path: "claims",
    canActivate: [AuthGuard],
    loadChildren: () => [
      {
        path: "",
        loadComponent: () => import("./claims/claims/claims.component").then(m => m.ClaimsComponent),
      },
      {
        path: 'carrier/:carrierId/:title',
        component: ClaimsComponent
      },
      {
        path: ':id',
        loadComponent: () => import("./claims/detail/claim-detail.component").then(m => m.ClaimDetailComponent),
        canActivate: [AuthGuard, AsmPermissionGuard],
        data: { permission: [Permissions.CLAIMS_MANAGE] }
      },
    ]
  },

  // Developer Routes (/developers)
  {
    path: "developers",
    canActivate: [AuthGuard, AsmPermissionGuard],
    //canActivateChild: [AuthGuard, AsmPermissionGuard],
    data: { permission: [Permissions.DEV_ALL] },
    loadChildren: () => [
      {
        path: "",
        loadComponent: () => import("./developers/developers.component").then(m => m.DevelopersComponent),
      },
      {
        path: "notifications",
        loadComponent: () => import("./developers/notifications/notifications.component").then(m => m.NotificationsComponent)
      },
      {
        path: "email-tester",
        loadComponent: () => import("./developers/email-tester/email-tester.component").then(m => m.EmailTesterComponent),
      },
      {
        path: "permissions",
        loadComponent: () => import("./developers/permissions/permissions.component").then(m => m.PermissionsComponent),
      },
      {
        path: "sql-extractor",
        loadComponent: () => import("./developers/sql-extractor/sql-extractor.component").then(m => m.SqlExtractorComponent),
      },
      {
        path: "api-logs",
        loadComponent: () => import("./developers/dev-api-logs/dev-api-logs.component").then(m => m.DevApiLogsComponent),
      },
      {
        path: "edi",
        children: [
          {
            path: '',
            redirectTo: 'files',
            pathMatch: 'full'
          },
          {
            path: 'files',
            component: EdiFilesComponent
          },
          {
            path: 'files/:filePath',
            component: EdiFileDetailComponent
          },
          {
            path: 'files/:fileName/:loadNumber',
            component: EdiLoadDetailComponent
          }
        ]
      },
    ],
  },

  // Locations Routes (/locations)
  {
    path: "locations",
    canActivate: [AuthGuard],
    loadChildren: () => [
      {
        path: "",
        loadComponent: () => import("./locations/list/locations.component").then(m => m.LocationsComponent),
      },
      {
        path: "import",
        loadComponent: () => import("./locations/import/locations.import.component").then(m => m.LocationsImportComponent)
      },
      {
        path: ":id",
        loadComponent: () => import("./locations/detail/location-detail.component").then(m => m.LocationDetailComponent)
      },
    ],
  },

  // My Profile Routes (/my-profile)
  {
    path: "my-profile",
    canActivate: [AuthGuard],
    loadComponent: () => import("./user-profile/user-profile.component").then(m => m.UserProfileComponent)
  },

  // Packages Routes (/admin/packages)
  {
    path: "admin/packages",
    canActivate: [AuthGuard],
    loadComponent: () => import("./packages/packages-list/packages.component").then(m => m.PackagesComponent)
  },

  // Reports Routes (/reports)
  {
    path: "reports",
    canActivate: [AuthGuard, AsmPermissionGuard], data: { permission: [Permissions.REPORTS_VIEW] },
    loadComponent: () => import("./reports/reports/reports.component").then(m => m.ReportsComponent)
  },

  // Shipments Routes (/shipments)
  {
    path: "shipments",
    canActivate: [AuthGuard],
    loadChildren: () => [
      {
        path: '',
        loadComponent: () => import("./shipments/list/shipments.component").then(m => m.ShipmentListComponent),
      },
      {
        path: '',
        loadComponent: () => import("./shipments/quotes/list/shipment-quote-list.component").then(m => m.ShipmentQuoteListComponent),
        outlet: "side"
      },
      {
        path: 'carrier/:carrierID/:title', component: ShipmentListComponent
      },
      {
        path: 'carrier/:carrierID/:title/:carrierPerformance', component: ShipmentListComponent
      },
      {
        path: 'lastDispatcher/:lastDispatcher', component: ShipmentListComponent
      },
      {
        path: 'originalDispatcher/:originalDispatcher', component: ShipmentListComponent
      },
      {
        path: 'traveldirection/:travelDirection', component: ShipmentListComponent
      },
      {
        path: 'currentstatus/:currentStatus', component: ShipmentListComponent
      },
      {
        path: 'performance/:performance', component: ShipmentListComponent
      },
      {
        path: 'performance-service-type/On-Time-Pickups/:serviceType', component: ShipmentListComponent
      },
      {
        path: 'performance-service-type/On-Time-Deliveries/:serviceType', component: ShipmentListComponent
      },
      {
        path: 'expedited/:expedited-supplier', component: ShipmentListComponent
      },
      {
        path: 'delivery-day/:delivery-day', component: ShipmentListComponent
      },
      {
        path: 'tripStatus/:tripStatus', component: ShipmentListComponent
      },
      {
        path: 'serviceType/:serviceType', component: ShipmentListComponent
      },
      {
        path: 'drilldown-default', component: ShipmentListComponent
      },
      {
        path: 'createdBy/:createdBy', component: ShipmentListComponent
      },
      {
        path: 'assignedBy/:assignedBy', component: ShipmentListComponent
      },
      {
        path: 'dispatch-dashboard-drilldown/dispatch-meta/:dispatch-meta', component: ShipmentListComponent
      },
      {
        path: 'dispatch-dashboard-drilldown/dispatch-exact-date/:dispatch-start-date', component: ShipmentListComponent
      },
      {
        path: 'dispatch-dashboard-drilldown/dispatch-date-range/:dispatch-start-date/:dispatch-end-date', component: ShipmentListComponent
      },
      {
        path: 'dispatch-dashboard-drilldown/dispatch-meta/:dispatch-meta/customer/:customerId', component: ShipmentListComponent
      },
      {
        path: 'dispatch-dashboard-drilldown/dispatch-exact-date/:dispatch-start-date/customer/:customerId', component: ShipmentListComponent
      },
      {
        path: 'dispatch-dashboard-drilldown/dispatch-date-range/:dispatch-start-date/:dispatch-end-date/customer/:customerId', component: ShipmentListComponent
      },
      {
        path: 'customer/:customerId', component: ShipmentListComponent
      },
      {
        path: 'customer/:customerId/createdBy/:createdBy', component: ShipmentListComponent
      },
      {
        path: 'customer/:customerId/assignedBy/:assignedBy', component: ShipmentListComponent
      },
      {
        path: 'new', component: ShipmentQuoteDetailComponent, canActivate: [AuthGuard, AsmPermissionGuard], data: { permission: [Permissions.SHIPMENTS_CREATE] }
      },
      {
        path: 'quotes', component: ShipmentQuoteListComponent, canActivate: [AuthGuard, AsmPermissionGuard], data: { permission: [Permissions.SHIPMENTS_CREATE] }
      },
      {
        path: 'requested', component: ShipmentQuoteListComponent
      },
      {
        path: 'quotes/quick',
        loadComponent: () => import("./shipments/quotes/quick-quote/quick-quote.component").then(m => m.QuickQuoteComponent),
        canActivate: [AuthGuard, AsmPermissionGuard],
        data: { permission: [Permissions.SHIPMENTS_QUICKQUOTE] }
      },
      {
        path: 'quotes/zip',
        loadComponent: () => import("./shipments/quotes/zip-quote/zip-quote.component").then(m => m.ZipQuoteComponent),
        canActivate: [AuthGuard, AsmPermissionGuard],
        data: { permission: [Permissions.SHIPMENTS_ZIPQUOTE] }
      },
      {
        path: 'quotes/multi',
        loadComponent: () => import("./shipments/quotes/multi-quote/multi-quote.component").then(m => m.MultiQuoteComponent),
        canActivate: [AuthGuard, AsmPermissionGuard],
        data: { permission: [Permissions.SHIPMENTS_MULTIQUOTE] }
      },
      {
        path: 'quotes/multi/:id',
        loadComponent: () => import("./shipments/quotes/multi-quote/multi-quote.component").then(m => m.MultiQuoteComponent),
        canActivate: [AuthGuard, AsmPermissionGuard],
        data: { permission: [Permissions.SHIPMENTS_MULTIQUOTE] }
      },
      {
        path: 'quotes/:id/scheduled', component: QuoteScheduledComponent
      },
      {
        path: 'quotes/:id', component: ShipmentQuoteDetailComponent
      },
      {
        path: 'inbound', component: InboundShipmentsComponent
      },
      {
        path: 'realtime-inbound', component: RealtimeInboundShipmentsComponent
      },
      {
        path: 'inbound/filter/:carrierName/:date', component: InboundShipmentsComponent
      },
      {
        path: 'inbound/filter/:date', component: InboundShipmentsComponent
      },
      {
        path: 'realtime-inbound/filter/:date', component: RealtimeInboundShipmentsComponent
      },
      {
        path: 'import', 
        component: ShipmentImportComponent, 
        canActivate: [AuthGuard, AsmPermissionGuard], 
        data: { permission: [Permissions.SHIPMENTS_IMPORT] }
      },
      {
        path: ':id',
        loadComponent: () => import("./shipments/details/details.component").then(m => m.ShipmentDetailsComponent)
      },
      {
        path: ':id/details', component: ShipmentDetailsComponent
      },
      {
        path: ':id/tracking', component: ShipmentDetailsComponent
      },
      {
        path: ':id/carrier-costs', component: ShipmentDetailsComponent, canActivate: [AuthGuard, AsmInternalGuard]
      },
      {
        path: ':id/customer-costs', component: ShipmentDetailsComponent
      },
      {
        path: ':id/billing', component: ShipmentDetailsComponent
      },
      {
        path: ':id/document-and-labels', component: ShipmentDetailsComponent
      },
      {
        path: ':id/notes', component: ShipmentDetailsComponent
      },
      {
        path: ':id/emails', component: ShipmentDetailsComponent
      },
      {
        path: ':id/audit-logs', component: ShipmentDetailsComponent
      },
      {
        path: ':id/api-logs', component: ShipmentDetailsComponent
      },
      {
        path: ':id/edi', component: ShipmentDetailsComponent
      },
      {
        path: ':id/contacts', component: ShipmentDetailsComponent
      },
      {
        path: ':id/lane-history', component: ShipmentDetailsComponent
      },
      {
        path: ':id/edit', component: ShipmentDetailsComponent
      },
      {
        path: ':id/notifications', component: ShipmentDetailsComponent
      }
    ]
  },

  // Blocked User Page
  {
    path: 'blocked-user',
    loadComponent: () => import("./blocked-user-page.component").then(m => m.BlockedUserPageComponent)
  },

  // Catch-All / Not Found
  {
    path: '**',
    pathMatch: 'full',
    loadComponent: () => import("./page-not-found.component").then(m => m.PageNotFoundComponent)
  },
];
